// Reads and writes of a delivery's pallets (delivery_pallets, mig 20261211000000).
// Kept out of deliveryPallets.ts so the pure rules (and their tests) never
// import the supabase client.

import type { SupabaseClient } from '@supabase/supabase-js'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout, withTimeout } from '@/lib/saveTimeout'
import { POLL } from '@/lib/pollTiers'
import type { DeliveryPallet } from './deliveryPallets'

const PALLET_COLS = 'pallet_id, delivery_id, addition_id, pallet_no, crates, gross_weight_kg, pallet_tare_kg, crate_tare_kg, net_weight_kg'

type Raw = Omit<DeliveryPallet, 'crates' | 'gross_weight_kg' | 'pallet_tare_kg' | 'crate_tare_kg' | 'net_weight_kg'> & {
  crates: number | string
  gross_weight_kg: number | string | null
  pallet_tare_kg: number | string
  crate_tare_kg: number | string | null
  net_weight_kg: number | string | null
}

const n = (v: number | string | null): number | null => (v == null ? null : Number(v))

function toPallet(r: Raw): DeliveryPallet {
  return {
    ...r,
    crates: Number(r.crates),
    gross_weight_kg: n(r.gross_weight_kg),
    pallet_tare_kg: Number(r.pallet_tare_kg ?? 0),
    crate_tare_kg: n(r.crate_tare_kg),
    net_weight_kg: n(r.net_weight_kg),
  }
}

/** A delivery's live pallets plus, for each, when it was tipped (null = not yet). */
export interface PalletWithTip extends DeliveryPallet { tipped_at: string | null }

/** Does this delivery have live pallets? A load with pallets joins only such a delivery. */
export async function deliveryHasPallets(db: SupabaseClient, deliveryId: string): Promise<boolean> {
  const { count, error } = await withReadTimeout(
    db.from('delivery_pallets').select('pallet_id', { count: 'exact', head: true })
      .eq('delivery_id', deliveryId).is('deleted_at', null),
    'delivery_pallets',
  )
  if (error) throw error
  return (count ?? 0) > 0
}

/** The live pallets of the given deliveries, in pallet order, with their tip time. */
export async function fetchPalletsFor(deliveryIds: readonly string[]): Promise<PalletWithTip[]> {
  if (deliveryIds.length === 0) return []
  const { data, error } = await withReadTimeout(
    supabase.from('delivery_pallets')
      .select(`${PALLET_COLS}, tipping_log(tipping_id, tipped_at)`)
      .in('delivery_id', [...deliveryIds])
      .is('deleted_at', null)
      .order('delivery_id').order('pallet_no'),
    'delivery_pallets',
  )
  if (error) throw error
  return ((data ?? []) as (Raw & { tipping_log: { tipping_id: string; tipped_at: string }[] | null })[]).map(r => {
    const { tipping_log, ...rest } = r
    return { ...toPallet(rest), tipped_at: tipping_log?.[0]?.tipped_at ?? null }
  })
}

/** One pallet by id (a scan of a sticker for a delivery not on the board yet). */
export async function fetchPallet(palletId: string): Promise<(PalletWithTip & { count: number }) | null> {
  const { data, error } = await withReadTimeout(
    supabase.from('delivery_pallets').select('delivery_id').eq('pallet_id', palletId).is('deleted_at', null).maybeSingle(),
    'delivery_pallets',
  )
  if (error) throw error
  if (!data) return null
  const all = await fetchPalletsFor([(data as { delivery_id: string }).delivery_id])
  const p = all.find(x => x.pallet_id === palletId)
  return p ? { ...p, count: all.length } : null
}

export const deliveryPalletsKey = (deliveryId: string) => ['delivery-pallets', deliveryId] as const

/** One delivery's pallets (Delivery detail, the sticker page). */
export function useDeliveryPallets(deliveryId: string | null | undefined, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: deliveryPalletsKey(deliveryId ?? ''),
    enabled: !!deliveryId,
    queryFn: () => fetchPalletsFor([deliveryId!]),
    refetchInterval: opts?.live ? POLL.live : false,
  })
}

/** Add (unknown id), edit or remove one pallet of a palleted delivery. */
export async function saveDeliveryPallet(db: SupabaseClient, a: {
  deliveryId: string
  palletId: string | null
  crates: number | null
  grossKg: number | null
  palletTareKg: number
  remove?: boolean
  reason?: string
}): Promise<void> {
  const { error } = await withTimeout(db.rpc('save_delivery_pallet', {
    p_delivery_id:     a.deliveryId,
    p_pallet_id:       a.palletId,
    p_crates:          a.crates,
    p_gross_weight_kg: a.grossKg,
    p_pallet_tare_kg:  a.palletTareKg,
    p_remove:          !!a.remove,
    p_reason:          a.reason ?? null,
  }), 'save_delivery_pallet')
  if (error) throw error
}
